import type { Request as BernieRequest } from 'bernie-http';
import type { Logger } from 'bernie-logger';

import type { SystemEvent } from '@shared-ui/logger-context';

interface LoggableJson {
  [key: string]: LoggableJson | boolean | string | number | null | undefined;
}

interface LoggableError extends Pick<Error, 'stack'> {
  [key: string]: string | number | undefined;
  errorMessage?: string;
  name: string;
  statusCode?: number;
  message: string;
}

const createErrorMetaData = (error: LoggableError) => ({
  statusCode: error.statusCode,
  errorMessage: error.message,
  stack: error.stack,
  name: error.name,
  message: error.message
});

const createContextualData = (request: BernieRequest): LoggableJson => {
  const { context, path, query, url } = request;

  return {
    domain: context.site.domain,
    url,
    path,
    queryParams: query ? JSON.stringify(query) : undefined,
    traceId: context.traceInfo?.['Trace-ID'],
    messageId: context.traceInfo?.['Message-ID'],
    deviceId: context.deviceId,
    siteId: context.site.id,
    brand: context.site.brand,
    tpid: context.site.tpid,
    eapid: context.site.eapid,
    isBot: context.bot,
    userAgent: context.userAgent,
    langId: context.langId,
    locale: context.locale,
    tuid: context.user.tuid,
    expUserId: context.user.expUserId,
    userState: context.user.authState,
    currency: context.currency,
    host: context.host,
    guid: context.guid,
    isMobile: context.deviceInformation?.mobile,
    isTablet: context.deviceInformation?.tablet
  };
};

export interface LogOptions {
  message?: string;
  request?: BernieRequest;
  otherMetaData?: LoggableJson;
  error?: LoggableError;
}

function isBernieRequest(request?: BernieRequest): request is BernieRequest {
  return Boolean(request?.context);
}

export function isLoggableError(error: unknown): error is LoggableError {
  return error instanceof Error;
}

function toStringOnBrowser(data: LoggableJson): LoggableJson | string {
  return typeof window === 'undefined' ? data : JSON.stringify(data);
}

export const logDetailedEvent = (logger: Logger, systemEvent: SystemEvent, options: LogOptions = {}): void => {
  logger.logEvent(
    systemEvent,
    options.message ? options.message : '',
    toStringOnBrowser(isBernieRequest(options.request) ? createContextualData(options.request) : {}),
    toStringOnBrowser(options.otherMetaData ? options.otherMetaData : {}),
    toStringOnBrowser(options.error ? createErrorMetaData(options.error) : {})
  );
};
